import React, { useState } from 'react';
import { ListItem, ListItemIcon, ListItemText, SvgIcon, Typography, Button, Icon } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AttachmentFileType } from '@kimuno/api-sdk/lib/types/responses/Common';

import { palette, spaces } from '../../assets/styles/themes/tokens';
import getMuiIcon, { IconName } from '../../utils/getMuiIcon';
import formatByteSize from '../../utils/formatByteSize';
import { AttachmentType } from './AttachmentsTableHeader';
import useScreenSize from '../../utils/useScreenSize';
import createNonPropagatingEventHandler from '../../utils/createNonPropagatingEventHandler';

interface AttachmentTableItemProps {
    item: {
        id: number;
        name: string;
        contentType: AttachmentFileType;
        type: string;
        contentPath: string;
        fileAmount: number;
        fileSize: number; // In bytes
    };
    onClickItemDownload: () => Promise<void>;
}

const ContentType: Record<AttachmentFileType, IconName> = {
  'unknown': 'defaultFileIcon',
  'wordDocument': 'docFileIcon',
  'spreadsheet': 'xlsFileIcon',
  'powerpoint': 'pptFileIcon',
  'pdf': 'pdfFileIcon',
  'image': 'jpgFileIcon',
  'video': 'mp4FileIcon',
  'audio': 'mp3FileIcon',
  'zip': 'folderIcon',
  'folder': 'folderIcon',
  'flash': 'defaultFileIcon',
};

const AttachmentTableItem = ({ item, onClickItemDownload }: AttachmentTableItemProps) => {
  const { t } = useTranslation();
  const {  isXsScreen, isSmScreen } = useScreenSize();
  const isSmallScreen = isXsScreen || isSmScreen;
  const isFolder = item.type.toLowerCase() === AttachmentType.FOLDER;
  const isShowFileIcon = isFolder || !isSmallScreen;

  const [isDownloadButtonDisabled, setIsDownloadButtonDisabled] = useState<boolean>(false);

  const onClickDownload = async () => {
    setIsDownloadButtonDisabled(true);
    await onClickItemDownload();
    setIsDownloadButtonDisabled(false);
  };

  // The prefix is a small detail item appearing under the attachment name
  const prefix = isFolder ? `${item.fileAmount} ${t('Files')}` :
    item.contentPath.split('.').pop()?.toUpperCase();

  return (
    <ListItem
      className={`attachment-item-${isFolder ? 'folder' : 'file'}`}
      sx={{
        borderRadius: spaces.md,
        border: `1px solid ${palette.elevation.outlined}`,
        backgroundColor: palette.background.paperElevation2,
        '&:hover': {
          backgroundColor: palette.background.paperElevation2,
        },
      }}
      secondaryAction={
        <Button
          data-testid={`attachment-download-button-${item.name.trim().replace(/\s+/g, '-')}`}
          className='download-attachment-button'
          variant='text'
          sx={{
            visibility: isSmallScreen ? 'visible' : 'hidden',
            opacity:  isSmallScreen ? 1: 0,
            minWidth: '32px',
          }}
          onClick={createNonPropagatingEventHandler(onClickDownload)}
          disabled={isDownloadButtonDisabled}
        >
          <SvgIcon
            component={getMuiIcon('downloadFileIcon')}
          />
        </Button>
      }
    >
      {isShowFileIcon && <ListItemIcon sx={{
        minWidth: '20px',
        marginRight: spaces.md
      }}>
        <Icon
          component={getMuiIcon(ContentType[item.contentType] || 'defaultFileIcon')}
          sx={{
            width: 20,
            height: 20,
            color: palette.text.primary
          }}
        />
      </ListItemIcon>}
      <ListItemText
        primary={
          <Typography
            className='attachment-title'
            variant='h6'
          >
            {item.name}
          </Typography>
        }
        secondary={
          <Typography
            variant='caption'
          >
            {`${prefix}, ${formatByteSize(item.fileSize)}`}
          </Typography>
        }
      />
    </ListItem>
  );
};

export default AttachmentTableItem;
